<template>
  <div class="secondaryFont w-full flex flex-column gap-4">
    <!-- first row -->
    <div class="rowField">
      <div class="inputContainer col-span-6">
        <label class="labelInput" for="legajo"
          >Legajo N° / N° de extracto <span class="required">*</span></label
        >
        <Skeleton v-if="legalDataStore.isLoading" height="46px" borderRadius="6px" />
        <InputText
          v-else
          class="input"
          :class="{ 'input-error': legajoNumeroError }"
          id="legajo"
          type="number"
          v-model="legalDataStore.formData.legajoNumero"
          :readonly="!legalDataStore.isEdit"
          @blur="validateLegajoNumero"
          @focus="handleFocusLegajo"
        />
        <span v-if="legajoNumeroError" class="error-message">{{ legajoNumeroError }}</span>
      </div>
      <div class="inputContainer col-span-3">
        <label class="labelInput" for="año">Año<span class="required"> *</span></label>
        <Skeleton v-if="legalDataStore.isLoading" height="46px" borderRadius="6px" />
        <Dropdown
          v-else
          id="año"
          v-model="legalDataStore.formData.anio"
          :options="yearOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Seleccione año"
          class="input"
          :class="{ 'input-error': anioError }"
          :disabled="!legalDataStore.isEdit"
          @show="handleShowAnio"
          @change="validateAnio"
        />
        <span v-if="anioError" class="error-message">{{ anioError }}</span>
      </div>
      <div class="inputContainer col-span-3">
        <label class="labelInput" for="sitio">Sitio<span class="required"> *</span></label>
        <Skeleton v-if="legalDataStore.isLoading" height="46px" borderRadius="6px" />
        <Dropdown
          v-else
          id="sitio"
          v-model="selectedSitioId"
          :options="sitiosList"
          optionLabel="Nombre"
          optionValue="TipoSitioID"
          placeholder="Seleccione sitio"
          class="input"
          :disabled="!legalDataStore.isEdit"
        />
      </div>
    </div>
    <!-- second row -->
    <div class="rowField">
      <!------------- Modus operandi -------------->
      <!-- !Expediente -->
      <div class="inputContainer col-span-6" v-if="!typeActuation.includes('Expediente')">
        <label class="labelInput" for="modusoperandi"
          >Modus operandi<span class="required"> *</span></label
        >
        <Skeleton v-if="legalDataStore.isLoading" height="46px" borderRadius="6px" />
        <Dropdown
          v-else
          id="modusoperandi"
          v-model="selectedModusOperandiId"
          :options="modusOperandiList"
          optionLabel="Nombre"
          optionValue="TipomodusoperandiID"
          placeholder="Seleccione modus operandi"
          class="input"
          :disabled="!legalDataStore.isEdit"
        />
      </div>

      <!-- Preliminar -->
      <div class="inputContainer col-span-3" v-if="typeActuation.includes('Preliminar')">
        <label class="labelInput" for="interviniente"
          >Interviniente<span class="required"> *</span></label
        >
        <Skeleton v-if="legalDataStore.isLoading" height="46px" borderRadius="6px" />
        <Dropdown
          v-else
          id="interviniente"
          v-model="legalDataStore.formData.juzgadoId"
          :options="juzgados"
          optionLabel="Nombre"
          optionValue="JuzgadoID"
          placeholder="Seleccione un interviniente"
          class="input"
          :disabled="!legalDataStore.isEdit"
        />
      </div>

      <!-- UFI -->
      <div
        class="col-span-6 grid-container"
        v-else-if="typeActuation.includes('Unidades Fiscales')"
      >
        <div class="inputContainer col-span-3">
          <label class="labelInput" for="ufi">UFI N°<span class="required"> *</span></label>
          <Skeleton v-if="legalDataStore.isLoading" height="46px" borderRadius="6px" />
          <Dropdown
            v-else
            v-model="legalDataStore.formData.ufiId"
            :options="ufis"
            optionLabel="Numero"
            optionValue="TipoufiID"
            placeholder="Seleccione UFI"
            class="input"
            :disabled="!legalDataStore.isEdit"
          />
        </div>
        <div class="inputContainer col-span-3">
          <label class="labelInput" for="fiscal"
            >Fiscal a Cargo<span class="required"> *</span></label
          >
          <Skeleton v-if="legalDataStore.isLoading" height="46px" borderRadius="6px" />
          <Dropdown
            v-else
            v-model="legalDataStore.formData.fiscalId"
            :options="fiscales"
            optionLabel="Denominacion"
            optionValue="PersonalfiscalID"
            placeholder="Seleccione fiscal"
            class="input"
            :disabled="!legalDataStore.isEdit"
          />
        </div>
      </div>

      <!-- Resto -->
      <div v-else class="inputContainer col-span-6">
        <label class="labelInput" for="juzgadoInterviniente"
          >Juzgado Interviniente<span class="required"> *</span></label
        >
        <Skeleton v-if="legalDataStore.isLoading" height="46px" borderRadius="6px" />
        <Dropdown
          v-else
          id="juzgadoInterviniente"
          v-model="legalDataStore.formData.juzgadoId"
          :options="juzgados"
          optionLabel="Nombre"
          optionValue="JuzgadoID"
          placeholder="Seleccione un juzgado"
          class="input"
          :disabled="!legalDataStore.isEdit"
        />
      </div>
    </div>

    <!-- third row -->
    <div class="rowField">
      <!------------- Causa/Carátula || Delito || Carátula del expediente -------------->
      <div class="inputContainer col-span-6">
        <label class="labelInput" for="causacaratula" v-if="typeActuation.includes('Expediente')">
          Carátula del expediente<span class="required"> *</span>
        </label>
        <label
          class="labelInput"
          for="causacaratula"
          v-else-if="
            !typeActuation.includes('Unidades Fiscales de Investigacion') &&
            !typeActuation.includes('UFI') &&
            !typeActuation.includes('Unidades Fiscales de Investigación')
          "
        >
          Causa/Carátula<span class="required"> *</span>
        </label>
        <label class="labelInput" for="causacaratula" v-else
          >Delito<span class="required"> *</span></label
        >

        <!-- Skeleton para chips -->
        <div v-if="legalDataStore.isLoading" class="flex gap-2">
          <Skeleton width="120px" height="32px" borderRadius="16px" />
          <Skeleton width="140px" height="32px" borderRadius="16px" />
          <Skeleton width="100px" height="32px" borderRadius="16px" />
        </div>

        <!-- Artículos (Expediente) -->
        <div v-else-if="typeActuation.includes('Expediente')">
          <MultiSelect
            v-if="legalDataStore.isEdit"
            v-model="legalDataStore.formData.articulos"
            :options="articulosList"
            optionLabel="Numeroarticulo"
            optionValue="ArticulosID"
            placeholder="Seleccione artículos"
            class="input"
            :maxSelectedLabels="3"
            @focus="handleFocusArticulo"
          >
            <template #value="slotProps">
              <template v-if="!slotProps.value || slotProps.value.length === 0">
                Seleccione artículos
              </template>
              <div v-else-if="slotProps.value.length < 3">
                <Chip
                  v-for="(articuloId, index) in slotProps.value"
                  :key="index"
                  :label="`Art. ${
                    articulosList.find((a) => a.ArticulosID === articuloId)?.Numeroarticulo ||
                    articuloId
                  }`"
                  class="mr-1"
                />
              </div>
              <div v-else>
                <Chip :label="`${slotProps.value.length} Seleccionados...`" />
              </div>
            </template>
          </MultiSelect>
          <!-- Chips en modo visualización -->
          <div v-else>
            <Chip
              v-for="articulo in legalData.articulos"
              :key="articulo.id"
              :label="`Art. ${articulo.numeroArticulo}`"
            />
          </div>
          <span v-if="articulosError && legalDataStore.isEdit" class="error-message">{{
            articulosError
          }}</span>
        </div>

        <!-- Causas/Carátulas (Otros tipos) -->
        <div v-else>
          <!-- MultiSelect en modo edición -->
          <MultiSelect
            v-if="legalDataStore.isEdit"
            v-model="legalDataStore.formData.causasCaratulas"
            :options="causasCaratulasList"
            optionLabel="Nombre"
            optionValue="TipocausacaratulaID"
            placeholder="Seleccione causas/carátulas"
            class="input"
            :maxSelectedLabels="3"
            @focus="handleFocusCausa"
          >
            <template #value="slotProps">
              <template v-if="!slotProps.value || slotProps.value.length === 0">
                Seleccione causas/carátulas
              </template>
              <div v-else-if="slotProps.value.length < 3">
                <Chip
                  v-for="(causaId, index) in slotProps.value"
                  :key="index"
                  :label="
                    causasCaratulasList.find((c) => c.TipocausacaratulaID === causaId)?.Nombre ||
                    causaId
                  "
                  class="mr-1"
                />
              </div>
              <div v-else>
                <Chip :label="`${slotProps.value.length} Seleccionados...`" />
              </div>
            </template>
          </MultiSelect>
          <!-- Chips en modo visualización -->
          <div v-else>
            <Chip
              v-for="causa in legalData.causasCaratulas"
              :key="causa.id"
              :label="causa.nombre"
            />
          </div>
          <span v-if="causasCaratulasError && legalDataStore.isEdit" class="error-message">{{
            causasCaratulasError
          }}</span>
        </div>
      </div>
      <div class="inputContainer col-span-6" v-if="typeActuation.includes('Unidades Fiscales')">
        <label class="labelInput" for="ayudante"
          >Ayudante Fiscal<span class="required"> *</span></label
        >
        <Skeleton v-if="legalDataStore.isLoading" height="46px" borderRadius="6px" />
        <Dropdown
          v-else
          v-model="legalDataStore.formData.ayudanteId"
          :options="ayudantesFiscales"
          optionLabel="Denominacion"
          optionValue="PersonalfiscalID"
          placeholder="Seleccione ayudante"
          class="input"
          :disabled="!legalDataStore.isEdit"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useStoreCompat, useRouterCompat } from "@/composables";
import { useActuacionWizardStore } from "@/store/Pinia/useActuacionWizardStore";
import { useCatalogs } from "@/composables/useCatalogs";
import { useJuzgadosStore } from "@/store/Pinia/useJuzgadosStore";
import { useUfisStore } from "@/store/Pinia/useUfisStore";
import { usePersonalFiscalStore } from "@/store/Pinia/usePersonalFiscalStore";
import { useLegalDataStore } from "@/store/Pinia/useLegalDataStore";
import { useModusOperandiStore } from "@/store/Pinia/useModusOperandiStore";
import { useSitioStore } from "@/store/Pinia/useSitioStore";
import { useArticulosStore } from "@/store/Pinia/useArticulosStore";
import { useCausaCaratulaStore } from "@/store/Pinia/useCausaCaratulaStore";
import { useDelitosStore } from "@/store/Pinia/useDelitosStore";
import { getYearOptions } from "@/utils/yearOptions";

const store = useStoreCompat();
const { route } = useRouterCompat();
const { preloadCatalogs } = useCatalogs();
const legalDataStore = useLegalDataStore();
const wizardStore = useActuacionWizardStore();
const sumario = computed(() => store.state.sumario);

const mapping = {
  "sumario-denuncia": "Sumario Prevencional Por Denuncia",
  "sumario-oficio": "Sumario Prevencional Por Oficio",
  "expediente-denuncia": "Expediente Contravencional por denuncia",
  "expediente-oficio": "Expediente Contravencional por oficio",
  "ufi-flagrancia": "Unidades Fiscales de Investigacion Flagrancia",
  "ufi-propiedad-denuncia":
    "Unidades Fiscales de Investigación Delitos Contra la Propiedad por Denuncia",
  "ufi-propiedad-oficio":
    "Unidades Fiscales de Investigación Delitos Contra la Propiedad por Oficio",
  "ufi-informatica-denuncia":
    "Unidades Fiscales de Investigación Delitos Informáticos y Estafas por Denuncia",
  "ufi-informatica-oficio":
    "Unidades Fiscales de Investigación Delitos Informáticos y Estafas por Oficio",
  "ufi-cavig": "Unidades Fiscales de Investigacion CAVIG",
  "ufi-anivi": "Unidades Fiscales de Investigacion ANIVI",
  "ufi-generica-denuncia": "Unidades Fiscales de Investigación Delitos Genéricos por Denuncia",
  "ufi-generica-oficio": "Unidades Fiscales de Investigación Delitos Genéricos por Oficio",
  "ufi-establecer-paradero":
    "Unidades Fiscales de Investigación Delitos Genéricos Establecer Paradero",
  "ufi-emergencias-medicas": "Unidades Fiscales de Investigacion D.E Emergencias Medicas",
  "ufi-siniestro-vial": "Unidades Fiscales de Investigacion D.E Siniestro Vial",
  "ufi-suicidio": "Unidades Fiscales de Investigacion D.E Ahorcamiento o Suicidio",
  "ufi-incendio-vivienda": "Unidades Fiscales de Investigacion D.E Incendio en Vivienda",
  "ufi-intoxicacion": "Unidades Fiscales de Investigacion D.E Intoxicacion",
  "ufi-otros": "Unidades Fiscales de Investigacion D.E Otros",
  "preliminares-incendio-vivienda": "Actuacion Preliminar por Incendio en Vivienda",
  "preliminares-establecer-procedencia": "Actuacion Preliminar para Establecer Procedencias",
  "preliminares-fuga-menor": "Actuacion Preliminar por Fuga de Menor",
  "preliminares-siniestro-vial": "Actuacion Preliminar por Siniestro Vial",
  "preliminares-caida-casual": "Actuacion Preliminar por Caida Casual",
  "preliminares-incendio-automotor": "Actuacion Preliminar por Incendio de Automotor",
  "preliminares-suicidio": "Actuacion Preliminar por Tentativa de Suicidio",
  "preliminares-otros": "Actuacion Preliminar - Otros",
};

function useKeyLabel() {
  const keyLabel = (texto = "") => {
    const normalized = texto.trim().replace(/\s+/g, " ");
    if (mapping[normalized]) {
      return mapping[normalized];
    }
    if (Object.values(mapping).includes(normalized)) {
      return normalized;
    }
    return normalized;
  };
  return { keyLabel };
}

const { keyLabel } = useKeyLabel();

const flowKey = computed(() => route.query?.type || "sumario-denuncia");

const typeActuation = computed(() => {
  let rawType = "";
  if (route.matched[0].path === "/newActuacion") {
    rawType = route.query?.type || "";
  } else {
    rawType = sumario.value?.formType || "";
  }

  return keyLabel(rawType);
});

const props = defineProps({
  mode: { type: String, default: "create" },
  actuacionId: { type: [String, Number], default: null },
  forceEdit: { type: Boolean, default: false },
});

const yearOptions = computed(() => {
  const baseOptions = getYearOptions();

  if (!legalDataStore.isEdit && legalDataStore.formData.anio) {
    const currentYear = legalDataStore.formData.anio;
    const yearExists = baseOptions.some((opt) => opt.value === currentYear);

    if (!yearExists) {
      return [{ value: currentYear, label: currentYear }, ...baseOptions].sort((a, b) =>
        a.value.localeCompare(b.value)
      );
    }
  }

  return baseOptions;
});

const legajoNumeroError = ref("");
const anioError = ref("");
const causasCaratulasError = ref("");
const articulosError = ref("");
const isTouched = ref(false);
const isAnioTouched = ref(false);

/**Se quitan maximo de articulos y causas  agregar la sigueinte linea en los multiselct para volver atras esta funcionalidad
 * :selectionLimit="MAX_ITEMS"
 * Gabriel navas 26/12/2025
 */
// const MAX_ITEMS = 4;

const handleFocusLegajo = () => {
  isTouched.value = true;
  validateLegajoNumero();
};

const handleFocusCausa = () => {
  isTouched.value = true;
  validateCausasCaratulas();
};

const handleFocusArticulo = () => {
  isTouched.value = true;
  validateArticulos();
};

const validateLegajoNumero = () => {
  legajoNumeroError.value = "";

  if (isTouched.value && !legalDataStore.formData.legajoNumero) {
    legajoNumeroError.value = "El número de legajo no puede estar vacío";
    return false;
  }

  return true;
};

const handleShowAnio = () => {
  isAnioTouched.value = true;
};

const validateAnio = () => {
  anioError.value = "";

  if (!legalDataStore.formData.anio) {
    if (isAnioTouched.value) {
      anioError.value = "El año no puede estar vacío";
    }
    return false;
  }

  return true;
};

const validateCausasCaratulas = () => {
  causasCaratulasError.value = "";

  if (isTouched.value) {
    if (
      !legalDataStore.formData.causasCaratulas ||
      legalDataStore.formData.causasCaratulas.length === 0
    ) {
      causasCaratulasError.value = typeActuation.value.includes("Expediente")
        ? "Debe seleccionar al menos una carátula"
        : typeActuation.value.includes("Unidades Fiscales")
          ? "Debe seleccionar al menos un delito"
          : "Debe seleccionar al menos una causa/carátula";
      return false;
    }
  }
  // if (legalDataStore.formData.causasCaratulas.length > MAX_ITEMS) {
  //   causasCaratulasError.value = `⚠️ DATOS LEGACY: Tiene ${legalDataStore.formData.causasCaratulas.length} items seleccionados. Debe reducir a máximo ${MAX_ITEMS} items antes de guardar`;
  //   return false;
  // }
  return true;
};

const validateArticulos = () => {
  articulosError.value = "";

  if (isTouched.value && legalDataStore.formData.articulos.length === 0) {
    articulosError.value = "Debe seleccionar al menos un artículo";
    return false;
  }
  return true;
};

const validateAllFields = () => {
  let isValid = true;

  isValid = validateLegajoNumero() && isValid;
  isValid = validateAnio() && isValid;

  if (typeActuation.value.includes("Expediente")) {
    isValid = validateArticulos() && isValid;
  } else {
    isValid = validateCausasCaratulas() && isValid;
  }

  if (typeActuation.value.includes("Unidades Fiscales")) {
    if (!legalDataStore.formData.ufiId) {
      isValid = false;
    }
    if (!legalDataStore.formData.fiscalId) {
      isValid = false;
    }
    if (!legalDataStore.formData.ayudanteId) {
      isValid = false;
    }
  } else {
    if (!legalDataStore.formData.juzgadoId) {
      isValid = false;
    }
  }

  legalDataStore.setFormValid(isValid);
  return isValid;
};

const juzgadosStore = useJuzgadosStore();
const legalData = computed(() => legalDataStore.getLegalData(typeActuation.value));

const juzgados = computed(() => {
  const depId = legalDataStore.datosLegales?.DependenciaID;
  const depNombre = legalDataStore.datosLegales?.DependenciaLetra;
  const juzgadoId =
    legalDataStore.formData.juzgadoId || legalData.value?.juzgadoInterviniente?.id || "";
  let base = [...juzgadosStore.allJuzgados];

  if (
    juzgadoId &&
    juzgadoId !== "DEPENDENCIA" &&
    !base.some((juzgado) => String(juzgado.JuzgadoID) === String(juzgadoId))
  ) {
    base = [
      { JuzgadoID: juzgadoId, Nombre: legalData.value?.juzgadoInterviniente?.nombre || "" },
      ...base,
    ];
  }

  if (typeActuation.value?.includes("Preliminar") && depId && depNombre) {
    base = [{ JuzgadoID: "DEPENDENCIA", Nombre: depNombre, esDependencia: true }, ...base];
  }
  return base;
});

watch(
  () => legalDataStore.datosLegales,
  (datos) => {
    const depId = datos?.DependenciaID;
    const depNombre = datos?.DependenciaLetra;
    if (
      typeActuation.value?.includes("Preliminar") &&
      depId &&
      depNombre &&
      !legalDataStore.formData.juzgadoId
    ) {
      legalDataStore.formData.juzgadoId = depId;
    }
  },
  { immediate: true, deep: true }
);

watch(
  () => legalDataStore.formData.legajoNumero,
  (newVal, oldVal) => {
    if (legalDataStore.isEdit && newVal !== oldVal) {
      legalDataStore.markFieldAsModified("legajoNumero");
    }
  }
);

watch(
  () => legalDataStore.formData.anio,
  (newVal, oldVal) => {
    if (legalDataStore.isEdit && newVal !== oldVal) {
      legalDataStore.markFieldAsModified("anio");
    }
  }
);

watch(
  () => legalDataStore.formData.causasCaratulas,
  (newVal, oldVal) => {
    if (legalDataStore.isEdit && newVal !== oldVal) {
      legalDataStore.markFieldAsModified("causasCaratulas");
    }
  },
  { deep: true }
);

watch(
  () => legalDataStore.formData.ufiId,
  (newVal, oldVal) => {
    if (legalDataStore.isEdit && newVal !== oldVal) {
      legalDataStore.markFieldAsModified("ufiId");
    }
  }
);

watch(
  () => legalDataStore.formData.fiscalId,
  (newVal, oldVal) => {
    if (legalDataStore.isEdit && newVal !== oldVal) {
      legalDataStore.markFieldAsModified("fiscalId");
    }
  }
);

watch(
  () => legalDataStore.formData.ayudanteId,
  (newVal, oldVal) => {
    if (legalDataStore.isEdit && newVal !== oldVal) {
      legalDataStore.markFieldAsModified("ayudanteId");
    }
  }
);

watch(
  () => legalDataStore.formData.juzgadoId,
  (newVal, oldVal) => {
    if (legalDataStore.isEdit && newVal !== oldVal) {
      legalDataStore.markFieldAsModified("juzgadoId");
    }
  }
);

watch(
  () => legalDataStore.formData.articulos,
  (newVal, oldVal) => {
    if (legalDataStore.isEdit && newVal !== oldVal) {
      legalDataStore.markFieldAsModified("articulos");
    }
  }
);

const ufisStore = useUfisStore();
const ufis = computed(() => ufisStore.ufis);

const personalFiscalStore = usePersonalFiscalStore();
const fiscales = computed(() => personalFiscalStore.fiscales);
const ayudantesFiscales = computed(() => personalFiscalStore.ayudantesFiscales);

const modusOperandiStore = useModusOperandiStore();
const modusOperandiList = computed(() => modusOperandiStore.modusOperandi);

const sitioStore = useSitioStore();
const sitiosList = computed(() => sitioStore.sitios);

const articulosStore = useArticulosStore();
const articulosList = computed(() => articulosStore.articulos);

const causaCaratulaStore = useCausaCaratulaStore();
const delitosStore = useDelitosStore();

// Computed que devuelve delitos o causas/carátulas según el tipo de actuación
const causasCaratulasList = computed(() => {
  const isUFI =
    typeActuation.value?.includes("Unidades Fiscales") || typeActuation.value?.includes("UFI");

  return isUFI ? delitosStore.delitos : causaCaratulaStore.causasCaratulas;
});

const legajoNumeroUnificado = computed(() => {
  return legalDataStore.buildLegajoNumero(
    legalDataStore.formData.legajoNumero,
    legalDataStore.formData.anio
  );
});

const selectedModusOperandiId = computed({
  get: () => legalDataStore.formData.modusOperandiId,
  set: (value) => {
    legalDataStore.formData.modusOperandiId = value;
    if (legalDataStore.isEdit) {
      legalDataStore.markFieldAsModified("modusOperandiId");
    }
  },
});

const selectedSitioId = computed({
  get: () => legalDataStore.formData.sitioId,
  set: (value) => {
    legalDataStore.formData.sitioId = value;
    if (legalDataStore.isEdit) {
      legalDataStore.markFieldAsModified("sitioId");
    }
  },
});

onMounted(async () => {
  if (route.matched[0].path != "/newActuacion") {
    legalDataStore.startView();
    const actuacionId = sumario.value?.IdActuacion || sumario.value?.id;
    if (actuacionId) {
      const isUFI =
        typeActuation.value?.includes("Unidades Fiscales") || typeActuation.value?.includes("UFI");
      const promises = [
        juzgadosStore.setAllJuzgados(),
        ufisStore.setUfis(),
        personalFiscalStore.setFiscales(),
        personalFiscalStore.setAyudantesFiscales(),
        modusOperandiStore.setModusOperandi(),
        sitioStore.setSitios(),
        articulosStore.setAllArticulos(),
      ];
      if (isUFI) {
        promises.push(delitosStore.setDelitos());
      } else {
        promises.push(causaCaratulaStore.setCausasCaratulas());
      }
      await Promise.all(promises);
      await legalDataStore.fetchDatosLegales(actuacionId);
      legalDataStore.initializeFormData(typeActuation.value);
      const data = legalData.value;
      const modusFromCatalog = modusOperandiList.value.find(
        (m) => m.Nombre === data.modusOperandi.nombre
      );
      if (modusFromCatalog) {
        legalDataStore.formData.modusOperandiId = modusFromCatalog.TipomodusoperandiID;
        const sitioFromCatalog = sitiosList.value.find((s) => s.Nombre === data.sitio.nombre);
        if (sitioFromCatalog) {
          legalDataStore.formData.sitioId = sitioFromCatalog.TipoSitioID;
        } else {
          console.warn("No se encontró ID de actuación en sumario:", sumario.value);
        }
      }
    }
  } else {
    legalDataStore.reset();
    await preloadCatalogs(flowKey.value);
    const existingWizardData = wizardStore.datosLegales?.data;
    const hasWizardData =
      existingWizardData &&
      Object.keys(existingWizardData).length > 0 &&
      (existingWizardData.legajoNumero ||
        existingWizardData.juzgadoId ||
        existingWizardData.causasCaratulas?.length > 0);
    if (hasWizardData) {
      Object.assign(legalDataStore.formData, existingWizardData);
      if (props.forceEdit) {
        legalDataStore.startEdit();
      }

      validateAllFields();

      wizardStore.setDatosLegalesData({ ...legalDataStore.formData });

      return;
    }

    if (props.mode === "edit" && props.actuacionId) {
      try {
        await legalDataStore.fetchDatosLegales(props.actuacionId);
        legalDataStore.initializeFormData(typeActuation.value);
      } catch (error) {
        console.error("Error en modo edición:", error);
        await inicializarComoCreacion();
      }
    } else {
      await inicializarComoCreacion();
    }
  }
});

async function inicializarComoCreacion() {
  const hasExistingData =
    legalDataStore.formData.legajoNumero ||
    legalDataStore.formData.juzgadoId ||
    legalDataStore.formData.causasCaratulas?.length > 0;

  if (!hasExistingData) {
    legalDataStore.initializeFormData(typeActuation.value);
  }

  if (props.forceEdit) {
    legalDataStore.startEdit();
  }

  legalDataStore.setFormValid(false);
}

watch(
  () => legalDataStore.isView,
  (isView) => {
    if (isView) {
      legajoNumeroError.value = "";
      causasCaratulasError.value = "";
      articulosError.value = "";
      legalDataStore.setFormValid(true);

      legalDataStore.initializeFormData(typeActuation.value);

      const data = legalData.value;

      const modusFromCatalog = modusOperandiList.value.find(
        (m) => m.Nombre === data.modusOperandi.nombre
      );
      if (modusFromCatalog) {
        legalDataStore.formData.modusOperandiId = modusFromCatalog.TipomodusoperandiID;
      }

      const sitioFromCatalog = sitiosList.value.find((s) => s.Nombre === data.sitio.nombre);
      if (sitioFromCatalog) {
        legalDataStore.formData.sitioId = sitioFromCatalog.TipoSitioID;
      }
    }
  }
);

defineExpose({
  validateAllFields,
});
</script>

<style scoped>
.inputContainer {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  position: relative;
}

.labelInput {
  font-weight: 500;
  font-style: Medium;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0px;
  color: #0b5195;
}

input {
  width: 100%;
  height: 46px;
  padding: 12px 16px 12px 20px;
  gap: 10px;
  border-radius: 6px;
  border: 1px solid #0b5195;
  opacity: 1;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0px;
  color: #0b5195;
}

.input-large {
  max-width: 548px;
}

.input-medium {
  max-width: 407px;
}

.rowField {
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  width: 100%;
  align-items: center;
  gap: 1rem;
}

.col-span-2 {
  grid-column: span 2;
}

.col-span-3 {
  grid-column: span 3;
}

.col-span-6 {
  grid-column: span 6;
}

.col-span-12 {
  grid-column: span 12;
}

.required {
  color: red;
}

.error-message {
  color: #dc2626;
  font-size: 12px;
  font-weight: 500;
  position: absolute;
  bottom: -18px;
  left: 0;
  display: block;
  white-space: nowrap;
}

.input-error {
  border-color: #dc2626 !important;
}

.input-error:focus {
  border-color: #dc2626 !important;
  box-shadow: 0 0 0 2px rgba(220, 38, 38, 0.1);
}

.grid-container {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 1rem;
  align-items: center;
}

:deep(.p-dropdown) {
  width: 100%;
  height: 46px;
  gap: 10px;
  border-radius: 6px;
  border: 1px solid #0b5195;
  opacity: 1;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0px;
  color: #0b5195;
}

:deep(.p-chip) {
  background-color: #e1ecf7;
  color: #0b5195;
  border-radius: 20px;
  padding: 8px 16px;
  margin-right: 8px;
  margin-bottom: 8px;
  font-family: IBM Plex Sans;
  font-weight: 700;
  font-style: Bold;
  font-size: 14px;
  line-height: 100%;
}

:deep(.p-chip .p-chip-text) {
  color: #0b5195;
  font-weight: 600;
}

:deep(.p-multiselect) {
  height: 60px;
  border-radius: 6px;
  border: 1px solid #0b5195;
}

:deep(.p-multiselect-panel) {
  max-width: 500px !important;
}

:deep(.p-multiselect-panel .p-multiselect-items .p-multiselect-item .p-multiselect-item-label) {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.error-message {
  color: #e74c3c;
  font-size: 14px;
  font-weight: 500;
  margin-top: -5px;
}

.input-error {
  border-color: #e74c3c !important;
}

.field-separator {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  font-weight: 400;
  color: #637381;
  margin: 0 12px;
  padding-top: 34px;
  min-width: 20px;
}
</style>
